import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { useNavigate } from "@tanstack/react-router";
import { useSession } from "@pontimed/auth-client";
import { MapPin } from "lucide-react";

import { cn } from "@pontimed/ui";

import { useAuthModal } from "@/components/auth/auth-modal-context";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { useDetectCountry } from "@/lib/geo/use-detect-country";
import { regionName } from "@/lib/region-name";

import globeSkin from "./country-globe-skin.module.css";
import { CountryComingSoonBanner } from "./country-coming-soon-banner";
import { CountryWaitlistSheet } from "./country-waitlist-sheet";
import { useCountriesQuery, type PickerCountry } from "./use-countries-query";

/**
 * Lazy-loaded globe — keeps the picker chunk out of the main bundle.
 * First-boot users (no cookie) trigger the download once; returning users
 * with a cookie never load it.
 */
const SvgGlobeMap = lazy(() =>
  import("@pontimed/ui/globe").then((m) => ({ default: m.SvgGlobeMap })),
);

export type CountryPickerMode = "blocking" | "dismissible";

interface CountryPickerSheetProps {
  open: boolean;
  mode: CountryPickerMode;
  /** Selected country (lowercase). Used to highlight on globe. */
  current?: string | null;
  /** Called with lowercase ISO 3166-1 alpha-2 when user picks a country */
  onSelect: (country: string) => void;
  /** Called when user dismisses (only invoked in `dismissible` mode) */
  onClose?: () => void;
}

function GlobeSkeleton() {
  return (
    <div className="bg-muted/40 flex h-full w-full items-center justify-center">
      <div className="border-primary/30 border-t-primary size-8 animate-spin rounded-full border-4" />
    </div>
  );
}

/**
 * Country picker built on top of `<ResponsiveSheet>` + the shared globe.
 * One component, two modes:
 *
 *   - `blocking`   — first-boot gate. Hides close button, backdrop tap is
 *                    a no-op, ESC ignored. User MUST pick before continuing.
 *   - `dismissible`— header switcher. X button + backdrop dismiss enabled.
 *
 * Globe is lazy-loaded so users with a `pontimed_country` cookie never pay
 * for the chunk. Country list is fetched from `/api/countries` (TanStack
 * Query, 5-min cache) and mapped to the globe's `{code, status}` shape.
 */
export function CountryPickerSheet({
  open,
  mode,
  current,
  onSelect,
  onClose,
}: CountryPickerSheetProps) {
  const { t, i18n } = useLingui();
  const { data: countries, isLoading } = useCountriesQuery();
  const { detect, loading: detectingLocation, status: detectStatus } = useDetectCountry();
  const { status: authStatus } = useSession();
  const authModal = useAuthModal();
  const navigate = useNavigate();

  // Country the user TAPPED on the globe but isn't `open`. Drives the footer
  // banner. Stays set until the user dismisses or picks an open country.
  const [previewedCountry, setPreviewedCountry] = useState<PickerCountry | null>(null);
  const [waitlistOpen, setWaitlistOpen] = useState(false);

  // Sheet stays mounted across open/close cycles, so without an explicit reset
  // a previously-previewed coming_soon country (e.g. Sweden) is still selected
  // on the globe + shown in the banner the next time the user opens the picker
  // even though their actual country is different.
  useEffect(() => {
    if (!open) {
      setPreviewedCountry(null);
      setWaitlistOpen(false);
    }
  }, [open]);

  const handleGlobeSelect = useCallback(
    (code: string) => {
      const cc = code.toLowerCase();
      const country = (countries ?? []).find((c) => c.code === code.toUpperCase());
      // Open countries close the picker (current contract). Anything else
      // (coming_soon / hidden) stays in preview — the banner takes over.
      if (!country || country.platformStatus === "open") {
        onSelect(cc);
        return;
      }
      setPreviewedCountry(country);
    },
    [countries, onSelect],
  );

  const handleUseLocation = useCallback(async () => {
    const cc = await detect();
    if (!cc) return;
    const country = (countries ?? []).find((c) => c.code === cc.toUpperCase());
    if (!country || country.platformStatus === "open") {
      onSelect(cc);
      return;
    }
    setPreviewedCountry(country);
  }, [detect, countries, onSelect]);

  const handleOpenReferral = useCallback(() => {
    if (authStatus === "authenticated") {
      void navigate({ to: "/account/user/wallet", search: { tab: "overview" } });
      return;
    }
    authModal.open({ returnTo: "/account/user/wallet?tab=overview" });
  }, [authStatus, authModal, navigate]);

  // Public palette (P4): the globe is coloured by the public status
  // (open / coming_soon / hidden) through the brand-token skin, not by the
  // internal rollout phase (seeding / directory / booking) admin uses.
  const globeCountries = useMemo(
    () =>
      (countries ?? []).map((c) => ({
        code: c.code,
        status: c.platformStatus,
      })),
    [countries],
  );

  const localizedNames = useMemo(
    () => buildLocalizedNames(countries, i18n.locale),
    [countries, i18n.locale],
  );

  // One-tap, keyboard-friendly list of the countries a user can pick now.
  const openCountries = useMemo(
    () =>
      (countries ?? [])
        .filter((c) => c.platformStatus === "open")
        .map((c) => ({
          code: c.code,
          flag: c.flag,
          name: localizedNames[c.code] || regionName(c.code, i18n.locale),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, i18n.locale)),
    [countries, localizedNames, i18n.locale],
  );

  // Globe highlight follows the preview if any, falling back to the actual
  // current country. Lets the user see what they tapped without us mutating
  // the parent's stored country.
  const selectedCode = previewedCountry
    ? previewedCountry.code
    : current
      ? current.toUpperCase()
      : null;

  const labels = {
    searchCountry: t`Search country`,
    zoomIn: t`Zoom in`,
    zoomOut: t`Zoom out`,
    resetPosition: t`Reset map`,
    searchCountryPlaceholder: t`Type to search...`,
    noCountryFound: t`No country found`,
    useMyLocation: t`Use my location`,
    close: t`Close`,
  };
  const statusLabels = { open: t`Available`, coming_soon: t`Coming soon` };

  const blocking = mode === "blocking";
  const handleClose = blocking ? () => {} : (onClose ?? (() => {}));

  return (
    <ResponsiveSheet
      open={open}
      onClose={handleClose}
      title={<Trans>Where are you?</Trans>}
      description={
        <Trans>
          Pick your country so we can show prices, providers and rules that apply to you.
        </Trans>
      }
      hideCloseButton={blocking}
      hideHandle={blocking}
      showBackdrop
      width="2xl"
      desktop="dialog"
      // History sentinel races with the URL writer that fires when the user
      // picks a country: outbound country-route-sync navigates with `replace`
      // before the sheet's cleanup runs `history.back()` to consume the
      // sentinel — the back then snaps the URL to the entry BEFORE the
      // sentinel (the original country), undoing the user's pick. The picker
      // already supports close via X / backdrop tap; back-button close isn't
      // worth the URL-flip regression.
      disableHistory
      // Fixed height on both viewports — body uses flex-1 + min-h-0 so without
      // a determined panel height the absolutely-positioned globe + locate
      // pill collapse to 0 (mobile bottom-sheet only renders the header).
      panelClassName="h-[85dvh] sm:h-[80dvh]"
      // p-0 sm:p-0 kills BOTH base AND sm-variant padding from the default
      // body class (`px-4 pt-3 pb-4 sm:px-6 sm:pt-4 sm:pb-6`). Without sm:p-0
      // the desktop dialog still has 1.5rem horizontal padding so the globe
      // sits in a narrower column instead of filling the modal edge to edge.
      // flex flex-col so the inner globe wrapper's flex-1 actually flexes.
      bodyClassName="relative flex flex-col overflow-hidden p-0 sm:p-0"
    >
      {/* Open countries as one-tap chips + the colour legend, above the map.
          The map stays the background of the sheet (no boxed panel). */}
      {openCountries.length > 0 && (
        <div className="shrink-0 px-4 pb-2 sm:px-6">
          <div
            role="group"
            aria-label={t`Available countries`}
            className="-mx-1 flex gap-2 overflow-x-auto px-1 py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {openCountries.map((c) => {
              const active = selectedCode === c.code;
              return (
                <button
                  key={c.code}
                  type="button"
                  aria-pressed={active}
                  onClick={() => onSelect(c.code.toLowerCase())}
                  className={cn(
                    "focus-visible:ring-ring/50 inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full border px-4 text-sm font-medium transition-colors outline-none focus-visible:ring-3",
                    active
                      ? "border-primary/40 bg-primary-soft text-primary"
                      : "border-border bg-card text-foreground hover:bg-muted",
                  )}
                >
                  {c.flag ? (
                    <span aria-hidden className="text-base leading-none">
                      {c.flag}
                    </span>
                  ) : null}
                  {c.name}
                </button>
              );
            })}
          </div>
          <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="bg-primary/60 size-2.5 rounded-full" />
              <Trans>Available</Trans>
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span aria-hidden className="bg-accent/45 size-2.5 rounded-full" />
              <Trans>Coming soon</Trans>
            </span>
          </div>
        </div>
      )}

      {/* Map fills the rest of the modal body. Two-level wrapper: outer is
          the flex slot, inner is `absolute inset-0` so `h-full` on the
          globe's own outer div has a concrete parent height to anchor
          against (a flex item alone doesn't propagate height to
          percentage-height descendants reliably across browsers). */}
      <div className="relative min-h-0 flex-1 overflow-hidden">
        {isLoading ? (
          <GlobeSkeleton />
        ) : (
          <Suspense fallback={<GlobeSkeleton />}>
            <div className={cn("absolute inset-0", globeSkin.skin)}>
              <SvgGlobeMap
                countries={globeCountries}
                selectedCode={selectedCode}
                onSelect={handleGlobeSelect}
                locale={i18n.locale}
                localizedNames={localizedNames}
                labels={labels}
                statusAttributes
                statusLabels={statusLabels}
                width={700}
                height={420}
              />
            </div>
          </Suspense>
        )}

        {/* Floating "Use my location" — top center over the map. inset-x-0 +
          flex-center makes the pill auto-center; pointer-events-none on the
          wrapper means clicks on the empty left/right areas pass through to
          the globe. z-10 sits above the map but BELOW the toolbar (z-20)
          so the toolbar always wins when their bounding boxes overlap. */}
        <div className="pointer-events-none absolute inset-x-0 top-3 z-10 flex flex-col items-center gap-2 px-4">
          <Button
            type="button"
            variant="default"
            className="pointer-events-auto min-h-[44px] gap-2 rounded-full px-5 py-2.5 text-sm font-semibold shadow-xl"
            onClick={handleUseLocation}
            disabled={detectingLocation}
          >
            <MapPin className="size-4" />
            {detectingLocation ? <Trans>Detecting...</Trans> : <Trans>Use my location</Trans>}
          </Button>
          {detectStatus === "denied" && (
            <p className="bg-card/90 text-muted-foreground pointer-events-auto rounded-full px-3 py-1 text-xs shadow-md backdrop-blur-sm">
              <Trans>Location access denied. Pick your country on the map below.</Trans>
            </p>
          )}
          {detectStatus === "error" && (
            <p className="bg-card/90 text-destructive pointer-events-auto rounded-full px-3 py-1 text-xs shadow-md backdrop-blur-sm">
              <Trans>Couldn&apos;t detect your country. Pick it on the map below.</Trans>
            </p>
          )}
        </div>
      </div>

      {/* Coming-soon banner — sticky at the bottom of the body when the user
          previewed a non-`open` country. Stays open until they dismiss or
          pick an actually-open country. shrink-0 so it doesn't compete with
          the globe's flex-1 slot for space. */}
      {previewedCountry && previewedCountry.platformStatus !== "open" && (
        <div className="shrink-0">
          <CountryComingSoonBanner
            country={previewedCountry}
            onDismiss={() => setPreviewedCountry(null)}
            onOpenWaitlist={() => setWaitlistOpen(true)}
            onOpenReferral={handleOpenReferral}
          />
        </div>
      )}

      <CountryWaitlistSheet
        country={previewedCountry}
        open={waitlistOpen}
        onClose={() => setWaitlistOpen(false)}
      />
    </ResponsiveSheet>
  );
}

function buildLocalizedNames(
  countries: PickerCountry[] | undefined,
  locale: string,
): Record<string, string> {
  if (!countries) return {};
  const out: Record<string, string> = {};
  for (const c of countries) {
    const localized = c.name[locale] || c.name.en || c.name.ro;
    if (localized) out[c.code] = localized;
  }
  return out;
}
